import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import SEO from "../components/SEO";

export default function Gallery() {
  const { category: urlCategory } = useParams();
  const [galleries, setGalleries] = useState([]);
  const [activeCategory, setActiveCategory] = useState("alle");
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    if (urlCategory) {
      const normalized = urlCategory === "trykproevning" ? "trykprovning" : urlCategory;
      setActiveCategory(normalized);
    }
  }, [urlCategory]);

  useEffect(() => {
    async function fetchGalleries() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/galleries");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            setGalleries(data);
          }
        }
      } catch (err) {
        console.error("Fejl ved hentning af gallerier:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGalleries();
  }, []);

  const getGalleryMeta = () => {
    if (activeCategory === "trykprovning" || activeCategory === "trykproevning") {
      return {
        title: "Galleri: BlowerDoor Trykprøvning & Tæthedsmåling | ET Danmark",
        description: "Se billeder og fotodokumentation fra BlowerDoor trykprøvninger og tæthedsmålinger udført af ET Danmark i Jylland og på Fyn.",
        canonical: "https://www.etdanmark.com/galleri/trykproevning",
        ogImage: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07384_05a504c6.jpg",
      };
    }
    if (activeCategory === "termografi") {
      return {
        title: "Galleri: Termografi & Varmetabsundersøgelser | ET Danmark",
        description: "Se termografiske målinger og infrarøde optagelser, der dokumenterer kuldebroer og isoleringsfejl i bygninger.",
        canonical: "https://www.etdanmark.com/galleri/termografi",
        ogImage: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07589_527e30c7.jpg",
      };
    }
    if (activeCategory === "energiramme") {
      return {
        title: "Galleri: Energirammeberegninger & Projekter | ET Danmark",
        description: "Se billeder og eksempler på bygningsprojekter og dokumentation for energirammeberegninger ved nybyggeri og renovering.",
        canonical: "https://www.etdanmark.com/galleri/energiramme",
        ogImage: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07445_ff41977b.jpg",
      };
    }
    return {
      title: "Galleri & Billeddokumentation | Se Vores Løsninger | ET Danmark",
      description: "Gå på opdagelse i vores galleri med eksempler og fotodokumentation inden for termografi, trykprøvning og energirammeberegning.",
      canonical: "https://www.etdanmark.com/galleri",
      ogImage: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07344-2_4f32161c.jpg",
    };
  };

  const currentMeta = getGalleryMeta();

  const categories = [
    { id: "alle", label: "Alle" },
    { id: "termografi", label: "Termografi" },
    { id: "trykprovning", label: "Trykprøvning" },
    { id: "energiramme", label: "Energiramme" },
    { id: "energimaerkning", label: "Energimærkning" },
    { id: "referencer", label: "Referencer" },
  ];

  // Filter items
  const filteredGalleries =
    activeCategory === "alle"
      ? galleries
      : galleries.filter((g) => g.category === activeCategory);

  // Flatten images with metadata
  const allImages = filteredGalleries.flatMap((g) =>
    (g.images || []).map((imgUrl) => ({
      url: imgUrl,
      category: g.category,
      galleryName: g.name,
      description: g.description,
    }))
  );

  // Keyboard navigation for lightbox
  useEffect(() => {
    function handleKeyDown(e) {
      if (activeImageIndex === null) return;
      if (e.key === "Escape") setActiveImageIndex(null);
      if (e.key === "ArrowRight") {
        setActiveImageIndex((prev) => (prev + 1) % allImages.length);
      }
      if (e.key === "ArrowLeft") {
        setActiveImageIndex(
          (prev) => (prev - 1 + allImages.length) % allImages.length
        );
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeImageIndex, allImages.length]);

  return (
    <div className="bg-[#FAF9F6] text-stone-800 min-h-screen">
      <SEO
        title={currentMeta.title}
        description={currentMeta.description}
        canonical={currentMeta.canonical}
        ogImage={currentMeta.ogImage}
      />
      {/* 1. TOP SLIDER HERO */}
      <PageTopSlider
        title="Galleri"
        subtitle="Billeder af vores arbejde i marken: BlowerDoor trykprøvninger, infrarød termografering, bygningsundersøgelser og energiramme projekter i hele Jylland og Fyn."
        badge="Dokumentation & Arbejdsbilleder"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Galleri" },
        ]}
        actions={
          <Link
            to="/kontakt"
            className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
          >
            <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
            <span>Kontakt for forespørgsel</span>
          </Link>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07344-2_4f32161c.jpg",
          alt: "BlowerDoor trykprøvning i marken",
        }}
      />

      {/* Filter Tabs & Gallery Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Categories Tab Bar */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 border-b border-stone-200 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  setActiveCategory(cat.id);
                  setActiveImageIndex(null);
                }}
                className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? "bg-[#165B4C] text-white shadow-xs"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-20 text-center text-stone-400">
              <i className="fa-solid fa-spinner fa-spin text-3xl mb-3 text-[#165B4C]"></i>
              <p className="text-sm">Indlæser billeder fra CMS...</p>
            </div>
          ) : allImages.length === 0 ? (
            <div className="py-20 text-center text-stone-500">
              <p>Ingen billeder fundet i denne kategori.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {allImages.map((item, index) => {
                const optimizedSrc = `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${item.url.replace(
                  "https://assets.auxodev.dk/",
                  ""
                )}`;

                return (
                  <div
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className="group relative aspect-4/3 rounded-2xl overflow-hidden bg-stone-100 border border-stone-200 shadow-2xs hover:shadow-lg transition-all cursor-pointer"
                  >
                    <img
                      src={optimizedSrc}
                      alt={`${item.galleryName} ${index + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-4 flex flex-col justify-between text-white">
                      <div className="self-end">
                        <span className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-xs flex items-center justify-center text-xs">
                          <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                        </span>
                      </div>
                      <div>
                        <span className="text-xs font-semibold capitalize bg-[#165B4C] px-2 py-0.5 rounded inline-block mb-1">
                          {item.category}
                        </span>
                        <p className="text-xs text-stone-200 line-clamp-1">
                          {item.galleryName}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && allImages[activeImageIndex] && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-stone-950/95 backdrop-blur-md flex items-center justify-center p-4 select-none animate-in fade-in duration-200"
          onClick={() => setActiveImageIndex(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[92vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="w-full flex items-center justify-between text-white pb-3 px-2">
              <div>
                <span className="text-sm font-semibold">
                  {allImages[activeImageIndex].galleryName}
                </span>
                <span className="text-xs text-stone-400 block">
                  Billede {activeImageIndex + 1} af {allImages.length}
                </span>
              </div>
              <button
                onClick={() => setActiveImageIndex(null)}
                className="p-2 text-stone-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Luk visning"
              >
                <i className="fa-solid fa-xmark text-2xl" aria-hidden="true"></i>
              </button>
            </div>

            {/* Modal Image Display */}
            <div className="relative w-full flex items-center justify-center overflow-hidden rounded-2xl bg-black/50">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/${allImages[
                  activeImageIndex
                ].url.replace("https://assets.auxodev.dk/", "")}`}
                alt="Forstørret billede"
                className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl"
              />

              {allImages.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImageIndex(
                      (prev) => (prev - 1 + allImages.length) % allImages.length
                    );
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center text-lg backdrop-blur-xs transition-all"
                  aria-label="Forrige billede"
                >
                  <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                </button>
              )}

              {allImages.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImageIndex(
                      (prev) => (prev + 1) % allImages.length
                    );
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center text-lg backdrop-blur-xs transition-all"
                  aria-label="Næste billede"
                >
                  <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                </button>
              )}
            </div>

            {/* Thumbnail preview row */}
            {allImages.length > 1 && (
              <div className="w-full flex items-center justify-center gap-2 mt-4 overflow-x-auto py-1 px-4 scrollbar-none">
                {allImages.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`w-14 h-14 rounded-lg overflow-hidden shrink-0 border-2 transition-all ${
                      idx === activeImageIndex
                        ? "border-emerald-400 scale-105"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=70,format=auto/${item.url.replace(
                        "https://assets.auxodev.dk/",
                        ""
                      )}`}
                      alt="Thumbnail"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
